<template>
    <card-list>
        <subheader-list-item icon="fa-align-left" title="Session Information" />

        <container-list-item padded no-border>
            <div class="columns">
                <div class="column">
                    <b-field label="Session Type" label-position="inside">
                        <b-select v-model="session.type" expanded>
                            <option value="live">Live (In-Person)</option>
                            <option value="remote">Live (Remote Broadcast)</option>
                            <option value="on-demand">On-Demand</option>
                        </b-select>
                    </b-field>
                </div>
            </div>

            <div class="columns">
                <div class="column">
                    <b-field label="Title" label-position="inside">
                        <b-input v-model="session.title" maxlength="250"></b-input>
                    </b-field>
                </div>
            </div>

            <div class="columns">
                <div class="column">
                    <b-field label="Presenter" label-position="inside">
                        <b-input v-model="session.presenter"></b-input>
                    </b-field>
                </div>
            </div>

            <div class="columns">
                <div class="column is-6">
                    <b-field label="Track/Topic" label-position="inside">
                        <b-select v-model="session.track_id" expanded icon-pack="fas" icon="code-branch">
                            <option :id="track.id" v-for="track in tracks" :value="track.id">{{ track.title }}</option>
                        </b-select>
                    </b-field>
                </div>
                <div class="column is-6">
                    <b-field label="Group" label-position="inside">
                        <b-select v-model="session.group_id" expanded icon-pack="fas" icon="layer-group">
                            <option :id="group.id" v-for="group in groups" :value="group.id">{{ group.title }}</option>
                        </b-select>
                    </b-field>
                </div>
            </div>

            <div class="columns">
                <div class="column">
                    <b-field label="Location (if applicable)" label-position="inside">
                        <b-input v-model="session.location" min="0"></b-input>
                    </b-field>
                </div>
                <div class="column is-3" v-if="session.type !== 'on-demand'">
                    <b-field label="Max Capacity" label-position="inside">
                        <b-input type="number" v-model="session.enroll_limit" min="0"></b-input>
                    </b-field>
                </div>
                <div class="column is-3" v-else>
                    <b-field label="Log Max Time (Minutes)" label-position="inside">
                        <b-input type="number" v-model="session.max_log_minutes" min="0"></b-input>
                    </b-field>
                </div>
            </div>

            <template v-if="session.type !== 'on-demand'">
                <div class="columns">
                    <div class="column">
                        <b-field label="Start Date & Time" label-position="inside">
                            <b-datetimepicker
                                v-model="session.start_at"
                                placeholder="Click to select..."
                                icon="calendar"
                                trap-focus
                                append-to-body
                                :min-datetime="conf.start_at"
                                :max-datetime="conf.end_at"
                                :editable="true"
                                :timepicker="timepicker"
                            >
                            </b-datetimepicker>
                        </b-field>
                    </div>
                    <div class="column">
                        <b-field label="End Date & Time" label-position="inside">
                            <b-datetimepicker
                                v-model="session.end_at"
                                placeholder="Click to select..."
                                icon="calendar"
                                trap-focus
                                append-to-body
                                :min-datetime="conf.start_at"
                                :max-datetime="conf.end_at"
                                :editable="true"
                                :timepicker="timepicker"
                            >
                            </b-datetimepicker>
                        </b-field>
                    </div>
                    <div class="column is-3">
                        <b-field label="Log Time (Minutes)" label-position="inside">
                            <b-input type="number" v-model="session.log_minutes" min="0"></b-input>
                        </b-field>
                    </div>
                </div>
                <div class="columns">
                    <div class="column">
                        <b-field>
                            <b-switch v-model="autoCalc">Auto Calculate End/Log Minutes</b-switch>
                        </b-field>
                    </div>
                </div>
            </template>

            <template v-if="session.type === 'remote'">
                <div class="columns">
                    <div class="column">
                        <b-field label="Remote Broadcast/Meeting Link" label-position="inside">
                            <b-input v-model="session.online_mtg_link" placeholder="https:// ..."></b-input>
                        </b-field>
                    </div>
                </div>
            </template>

            <template v-else-if="session.type === 'on-demand'">
                <div class="columns" v-if="video.url">
                    <div class="column">
                        <div class="video-frame">
                            <video-player
                                ref="videoPlayer"
                                :options="playerOptions"
                                v-if="video.url"
                            />
                        </div>
                    </div>
                </div>

                <div class="columns">
                    <div class="column">
                        <b-button @click="mediaPicker=true"><i class="far fa-film mr-2"></i>Choose Video</b-button>
                    </div>
                </div>

                <media-picker class="library" type="VIDEO" @choose="chooseMedia" @close="mediaPicker=false" v-if="mediaPicker"></media-picker>
            </template>

        </container-list-item>

        <container-list-item padded no-border>
            <b-field label="Description" message="Description is displayed to participant before they 'plan to attend' or on on-demand menu screen.">
                <quill-editor v-model="session.description" ref="descriptionEditor" :options="editorOptions" class="has-background-white content" />
            </b-field>
        </container-list-item>

        <container-list-item padded no-border>
            <b-field label="Information" message="Information is displayed to participants after they 'plan to attend' or on on-demand video screen.">
                <quill-editor v-model="session.information" ref="informationEditor" :options="editorOptions" class="has-background-white content" />
            </b-field>
        </container-list-item>

        <container-list-item padded no-border>
            <b-field label="Session Materials/Files">
                <section v-if="session.id">
                    <div class="session-file" v-for="file in session.files">
                        <div class="columns">
                            <div class="column pt-4"><i class="far fa-file mr-2"></i>{{ file.title }}</div>
                            <div class="column is-narrow">
                                <b-button tag="a" :href="file.url" target="_blank" class="is-small is-outlined">
                                    <i class="fad fa-share-from-square"></i>
                                </b-button>
                            </div>
                            <div class="column is-narrow">
                                <b-button @click="removeFile(file)" class="is-small is-danger is-outlined">
                                    <i class="fad fa-trash-alt"></i>
                                </b-button>
                            </div>
                        </div>
                    </div>
                    <b-button @click="filePicker=true" class="mt-4"><i class="far fa-file mr-2"></i>Choose File</b-button>
                </section>
                <p v-else class="is-italic">
                    <i class="fal fa-info-circle mr-2"></i>You must create the session before you can add session materials.
                </p>
            </b-field>

            <media-picker class="library" type="FILE" @choose="chooseFile" @close="mediaPicker=false" v-if="filePicker"></media-picker>

        </container-list-item>

        <container-list-item padded no-border>
            <div class="columns">
                <div class="column">
                    <b-field label="Allowed Registration Types" message="Choose all that apply.  Select multiple with control/shift key.  If nothing is chosen then all are allowed.">
                        <b-select v-model="session.allowed_types" native-size="5" multiple expanded>
                            <option :id="type.id" v-for="type in types" :value="type.id">{{ type.title }}</option>
                        </b-select>
                    </b-field>
                </div>
            </div>
        </container-list-item>

        <subheader-list-item icon="fa-align-left" title="Advanced Settings" />

        <container-list-item padded no-border>
            <div class="columns">
                <div class="column is-3">
                    <b-field>
                        <b-switch v-model="session.is_visible" true-value="1" false-value="0">Visible</b-switch>
                    </b-field>
                </div>
                <div class="column is-3">
                    <b-field>
                        <b-switch v-model="session.is_active" true-value="1" false-value="0">Active</b-switch>
                    </b-field>
                </div>
            </div>
        </container-list-item>

    </card-list>
</template>

<script>
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import CardListItem from "@/TIER/components/CardListItem";
    import SeparatorListItem from "@/components/common/SeparatorListItem";
    import async from "async";

    import moment from "moment";
    import {client as http} from "../../../http_client";
    import MediaPicker from "../../media_library/MediaPicker";
    import {videoPlayer} from 'vue-video-player';

    import 'video.js/dist/video-js.css'

    export default {
        name: 'ConfSessionForm',
        props: {
            conf: {
                type: Object,
                required: true
            },
            session: {
                type: Object,
                required: true
            },
        },
        components: {MediaPicker, SeparatorListItem, CardListItem, ContainerListItem, SubheaderListItem, CardList, videoPlayer},
        data: function () {
            return {
                type: null,
                title: null,
                componentSelect: null,
                components: [],
                editorOptions: {},
                mediaPicker: false,
                filePicker: false,
                tracks: [],
                groups: [],
                types: [],

                video: {},

                timepicker: {
                    incrementMinutes: 5
                },
                autoCalc: true
            };
        },
        computed: {
            playerOptions: function () {
                return {
                    // videojs options
                    muted: false,
                    fluid: true,
                    language: 'en',
                    poster: this.video.poster,

                    playbackRates: [0.7, 1.0, 1.5, 2.0],
                    sources: [{
                        type: this.video.mime_type,
                        src: this.video.url
                    }],
                };
            }
        },
        methods: {
            load: function (force) {
                async.parallel([
                    (cb) => {
                        http.get('conferences/' + this.conf.id + '/tracks', {force}).then(response => {
                            this.tracks = response.data;
                            cb();
                        }).catch(err => {
                            cb(err);
                        });
                    },
                    (cb) => {
                        http.get('conferences/' + this.conf.id + '/groups', {force}).then(response => {
                            this.groups = response.data;
                            cb();
                        }).catch(err => {
                            cb(err);
                        });
                    },
                    (cb) => {
                        http.get('conferences/' + this.conf.id + '/types', {force}).then(response => {
                            this.types = response.data;
                            cb();
                        }).catch(err => {
                            cb(err);
                        });
                    },
                    (cb) => {
                        this.video = this.session.media || {};
                        cb();
                    }
                ], err => {

                });
            },
            chooseMedia: function (media) {
                this.mediaPicker = false;

                delete media.updated_at;
                delete media.created_at;
                delete media.org_id;
                this.video = media;

                this.session.media_id = media.id;
            },
            chooseFile: function (file) {
                this.filePicker = false;
                console.log(file)

                http.post('conferences/' + this.conf.id + '/sessions/' + this.session.id + '/files', {session_id: this.session.id, library_file_id: file.id}).then(response => {
                    this.session.files.push({
                        id: response.data.id,
                        title: file.title,
                        type: file.type
                    });
                }).catch(err => {
                    console.error(err);
                });
            },
            removeFile: function (file) {
                http.delete('conferences/' + this.conf.id + '/sessions/' + this.session.id + '/files/' + file.id).then(response => {
                    this.session.files = this.session.files.filter(f => f.id !== file.id);
                }).catch(err => {
                    console.error(err);
                });
            }
        },
        watch: {
            'session.group_id': async function () {
                if (this.autoCalc && !this.session.id) {
                    let group = this.groups.find(g => g.id === this.session.group_id);

                    this.autoCalc = false;
                    this.session.log_minutes = group.log_minutes;

                    if (group.start_at && group.end_at) {
                        this.session.start_at = await moment(group.start_at).toDate();
                        this.session.end_at = await moment(group.end_at).toDate();
                    }

                    this.autoCalc = true;
                }
            },
            'session.end_at': function () {
                if (this.autoCalc && this.session.start_at) {
                    let start = moment(this.session.start_at);
                    let end = moment(this.session.end_at);
                    let diff = end.diff(start, 'minutes');
                    this.session.log_minutes = diff;
                }
            },
            'session.log_minutes': function () {
                if (this.autoCalc && this.session.start_at) {
                    let start = moment(this.session.start_at);
                    let end = start.clone().add(this.session.log_minutes, 'minutes');
                    this.session.end_at = end.toDate();
                }
            }
        },
        mounted: function () {
            this.load();
        }
    };
</script>

<style scoped lang="scss">
    .video-frame {
        border-radius: 5px;
        margin: 0;
        border: 1px solid #e7e7e7;
        padding: 10px;

    }

    .session-file {
        border: 1px solid #cccccc;
        background: white;
        padding: 7px 15px;
        border-radius: 8px;
        font-size: 15px;
        font-weight: bold;
        margin-bottom: 10px;
    }
</style>
